<template>
  <div class="grid-x grid-margin-x" :class="spacingClasses">
    <div v-if="isMobile" class="cell title-mobile padding-bottom-xs">
      <ProductTitle />
      <ReviewRating v-if="!isGiftWithPurchase" />
    </div>
    <div :class="cellLeft">
      <Media />
    </div>
    <div :class="cellRight">
      <div v-if="!isMobile" class="title">
        <ProductTitle />
        <ReviewRating v-if="!isGiftWithPurchase" class="margin-bottom-m" />
      </div>
      <div v-if="newMarkdown" class="small medium-margin-bottom-m small-margin-top-m">
        {{ newMarkdown }}
      </div>
      <OffersAvailable v-if="isBcom && !isMobile" />
      <ColorSwatches
        v-if="!isGiftWithPurchase && isMcom && isMultiColor"
        :id="productId"
        :color-label="`COLORS IN THIS COLLECTION`"
        :color-props="colorProps"
        class="margin-bottom-m small-margin-top-m medium-margin-top-z"
      />
      <ShopMembersButton
        :class="{
          'margin-top-m': isGiftWithPurchase,
          'small-margin-top-m medium-margin-top-z': !isGiftWithPurchase && (isBcom || !isMultiColor),
        }"
      />
      <LoyalList v-if="isLoyalList" class="margin-top-xs margin-bottom-m" />
      <ProsZoneAVertical v-if="!isMobile" data-testid="pros-section" class="margin-top-m" />
    </div>
    <div class="cell large-12" data-testid="product-details-section">
      <ProductDetails
        :class="{
          'margin-top-xl': !isMobile,
          'margin-top-m': isMobile,
        }"
      />
    </div>
  </div>

  <!-- shared / reusable template for the member list -->
  <DefineMemberProductList v-slot="{ list }">
    <ListOfMemberProducts :list>
      <template #skeleton-loaders="props">
        <MemberSkeletonTemplate v-if="loadMemberProductSkeleton" v-bind="props" />
      </template>
    </ListOfMemberProducts>
  </DefineMemberProductList>

  <div id="members-of-master">
    <LatchKey v-if="isLatchKey">
      <template #member-products-list="{ list }">
        <ReuseMemberProductList :list />
      </template>
    </LatchKey>

    <template v-else>
      <h2
        :class="{
          h3: !isMobile,
          h4: isMobile,
          'margin-bottom-s': shoppableRedesignOptimization,
          'margin-bottom-m': !shoppableRedesignOptimization,
        }"
      >
        Choose Your Items ({{ memberProductIds.length }})
      </h2>
      <ReuseMemberProductList :list="memberProductIds" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, onBeforeMount } from 'vue';
import { defineLazyVisibleHydratedComponent } from '@nuxt-common/media-card-large/app/modules/lazy-hydration';
import { createReusableTemplate } from '@vueuse/core';
import useMembersOfProduct from '~/composables/useMembersOfProduct';
import ProductTitle from '~/components/productTitle/ProductTitle.common.vue';
import StaticImportListOfMemberProducts from '~/components/ProductCard/ListOfMemberProducts.vue';
import OffersAvailable from '~/components/ProductCard/OffersAvailable.vue';
import StaticImportProductDetails from '~/modules/productDetails/components/ProductDetails';
import LazyHydratedProductDetails from '~/modules/lazy-hydration/components/ProductDetails';
import ReviewRating from '~/modules/reviews/components/ReviewRating';
import Media from '~/components/common/Media/Media.vue';
import LatchKey from '~/components/LatchKey.vue';
import ShopMembersButton from '~/components/ShopMembersButton.vue';
import ColorSwatches from '~/modules/color/ColorSwatches.vue';
import LoyalList from '~/components/LoyalList.bcom.vue';
import ProsZoneAVertical from '~/modules/pros/ProsZoneAVertical';
import useColor from '~/modules/color/composables/useColor';

import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useProductType } from '~/composables/useProductType';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useBrand } from '~/composables/useBrand';
import useNewMarkdownMaster from '~/composables/useNewMarkdownMaster';
import useGridLayoutConfig from '~/composables/useGridLayoutConfig';
import { preFetchAsyncComponents } from '~/utils/preFetchAsyncComponents';

const { isMobile } = useDeviceType();
const { isLatchKey, isGiftWithPurchase } = useProductType();
const { isMcom, isBcom } = useBrand();
const { isLoyalList, isMultiColor, shoppableRedesignOptimization } = useFeatureEligibility();
const { memberProductIds } = useMembersOfProduct();
const { productId } = useProduct();

const { colorProps } = useColor();
const { newMarkdown } = useNewMarkdownMaster();
const spacingClasses: string = shoppableRedesignOptimization ? 'small-margin-bottom-m large-margin-bottom-xl' : 'margin-bottom-xxl';
const { cellLeft, cellRight } = useGridLayoutConfig();

const [DefineMemberProductList, ReuseMemberProductList] = createReusableTemplate();

const {
  killswitches: { enableClientSideMemberListPerfImprovement, isLazyHydrationEnabled },
} = useKillswitches();

const loadMemberProductSkeleton = enableClientSideMemberListPerfImprovement || isLazyHydrationEnabled;

let ProductDetails = StaticImportProductDetails,
  ListOfMemberProducts = StaticImportListOfMemberProducts;

// Note: remove later once lazy hydration testing is over
if (isLazyHydrationEnabled) {
  ProductDetails = LazyHydratedProductDetails;
  ListOfMemberProducts = defineLazyVisibleHydratedComponent(() => import('~/components/ProductCard/ListOfMemberProducts.vue'));
}

const MemberSkeletonTemplate = defineAsyncComponent(() => import('~/components/common/skeleton-templates/MemberProductSkeleton.vue'));

if (loadMemberProductSkeleton) {
  /**
   * Pre-fetches the MemberProductSkeleton component chunk if it's likely to be displayed.
   * This is an optimization to load the skeleton loader component ahead of time,
   * improving perceived performance when the user scrolls to the member products list.
   * The chunk is loaded only if the product is a Latch Key or has more than 10 member products,
   * as these are cases where skeleton loaders are most likely to be seen by the user.
   */
  const preFetchMemberListSkeleton = () => {
    if (isLatchKey || memberProductIds.value.length > 10) {
      preFetchAsyncComponents(MemberSkeletonTemplate);
    }
  };

  onBeforeMount(preFetchMemberListSkeleton);
}
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@include title;
</style>
